import React, { useState, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  useTheme,
  Alert,
  Chip,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

/**
 * Parses the CSV text into a structured object.
 *
 * Expected CSV format:
 *   ticker,2019-01-31,2019-02-28,...
 *   AAPL,0.07,0.1,...
 *   MSFT,-0.04,0.25,...
 *
 * Returns:
 *   {
 *     AAPL: { "2019-01-31": 0.07, "2019-02-28": 0.1, ... },
 *     MSFT: { "2019-01-31": -0.04, ... }
 *   }
 *   Empty cells are stored as null.
 */
function parseHistoricalSignalsCsv(csvText) {
  const lines = csvText
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  if (lines.length < 2) {
    throw new Error("CSV must contain a header row and at least one data row.");
  }

  const [headerLine, ...dataLines] = lines;
  const headers = headerLine.split(",");

  // First header cell should be "ticker" (case-insensitive) or similar
  const [, ...dates] = headers.map((h) => h.trim());

  if (dates.length === 0) {
    throw new Error("CSV must contain at least one date column after the ticker column.");
  }

  const result = {};

  dataLines.forEach((line, lineIndex) => {
    const cells = line.split(",");
    const ticker = cells[0]?.trim();

    if (!ticker) return; // skip empty rows

    result[ticker] = {};

    dates.forEach((date, i) => {
      const rawValue = cells[i + 1]?.trim();
      if (rawValue === undefined || rawValue === "") {
        result[ticker][date] = null;
      } else {
        const num = parseFloat(rawValue);
        result[ticker][date] = isNaN(num) ? null : num;
      }
    });
  });

  if (Object.keys(result).length === 0) {
    throw new Error("No valid ticker rows found in the CSV.");
  }

  return { dates, data: result };
}

const UploadHistoricalSignalsModal = ({ open, onClose, onUpload }) => {
  const theme = useTheme();
  const fileInputRef = useRef(null);

  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState(null);
  const [parsedResult, setParsedResult] = useState(null); // { dates, data }
  const [parseError, setParseError] = useState(null);

  const reset = () => {
    setFileName(null);
    setParsedResult(null);
    setParseError(null);
    setDragOver(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const processFile = useCallback((file) => {
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".csv")) {
      setParseError("Only CSV files are accepted.");
      setFileName(null);
      setParsedResult(null);
      return;
    }

    setFileName(file.name);
    setParseError(null);
    setParsedResult(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const result = parseHistoricalSignalsCsv(text);
        setParsedResult(result);
        setParseError(null);
      } catch (err) {
        setParseError(err.message);
        setParsedResult(null);
      }
    };
    reader.onerror = () => {
      setParseError("Failed to read the file.");
    };
    reader.readAsText(file);
  }, []);

  // Drag & drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleUpload = () => {
    if (!parsedResult) return;
    onUpload(parsedResult.data);
    handleClose();
  };

  const tickerCount = parsedResult ? Object.keys(parsedResult.data).length : 0;
  const dateCount = parsedResult ? parsedResult.dates.length : 0;

  const dropZoneBorder = dragOver
    ? theme.palette.primary.main
    : parseError
    ? theme.palette.error.main
    : parsedResult
    ? theme.palette.success.main
    : theme.palette.divider;

  const dropZoneBg = dragOver
    ? theme.palette.mode === "dark"
      ? "rgba(144,202,249,0.08)"
      : "rgba(25,118,210,0.05)"
    : "transparent";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          pb: 1,
        }}
      >
        <Typography
          variant="h5"
          component="span"
          sx={{ fontWeight: 600, fontSize: "1.3rem", flex: 1 }}
        >
          Upload Historical Signals
        </Typography>
        <IconButton size="small" onClick={handleClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        {/* Drop Zone */}
        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            border: `2px dashed ${dropZoneBorder}`,
            borderRadius: 2,
            backgroundColor: dropZoneBg,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            py: 5,
            px: 3,
            cursor: "pointer",
            transition: "border-color 0.2s, background-color 0.2s",
            "&:hover": {
              borderColor: theme.palette.primary.main,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(144,202,249,0.08)"
                  : "rgba(25,118,210,0.05)",
            },
          }}
        >
          {parsedResult ? (
            <CheckCircleIcon
              sx={{ fontSize: 48, color: theme.palette.success.main }}
            />
          ) : (
            <UploadFileIcon
              sx={{
                fontSize: 48,
                color: dragOver
                  ? theme.palette.primary.main
                  : theme.palette.text.secondary,
              }}
            />
          )}

          <Typography
            variant="body1"
            sx={{ fontWeight: 500, textAlign: "center" }}
          >
            {parsedResult
              ? "File loaded successfully"
              : dragOver
              ? "Release to upload"
              : "Drag & drop a CSV file here"}
          </Typography>

          {!parsedResult && (
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary }}
            >
              or click to browse
            </Typography>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: "none" }}
            onChange={handleFileInputChange}
            onClick={(e) => e.stopPropagation()}
          />
        </Box>

        {/* File name */}
        {fileName && (
          <Box
            sx={{
              mt: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <InsertDriveFileIcon
              fontSize="small"
              sx={{ color: theme.palette.text.secondary }}
            />
            <Typography variant="body2" noWrap sx={{ flex: 1 }}>
              {fileName}
            </Typography>
            <Button size="small" onClick={reset} sx={{ textTransform: "none" }}>
              Remove
            </Button>
          </Box>
        )}

        {/* Parse error */}
        {parseError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {parseError}
          </Alert>
        )}

        {/* Summary */}
        {parsedResult && (
          <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip
              size="small"
              label={`${tickerCount} ticker${tickerCount !== 1 ? "s" : ""}`}
              color="primary"
              variant="outlined"
            />
            <Chip
              size="small"
              label={`${dateCount} date column${dateCount !== 1 ? "s" : ""}`}
              color="primary"
              variant="outlined"
            />
          </Box>
        )}

        {/* Format hint */}
        {!fileName && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 2,
              color: theme.palette.text.secondary,
              lineHeight: 1.6,
            }}
          >
            Expected format: the first column must be <strong>ticker</strong>,
            and the remaining columns must be dates (e.g.{" "}
            <code>2019-01-31</code>). Each row represents a ticker and its
            signal values for each date.
          </Typography>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          className="upload-historical-cancel-btn"
          variant="outlined"
          onClick={handleClose}
          sx={{
            textTransform: "none",
            color: theme.palette.error.main,
            borderColor: theme.palette.error.main,
            "&.MuiButton-outlined": {
              color: theme.palette.error.main,
              borderColor: theme.palette.error.main,
              boxShadow: "none",
            },
            "&:hover": {
              color: `${theme.palette.error.main} !important`,
              borderColor: `${theme.palette.error.dark} !important`,
              background: theme.palette.mode === "dark"
                ? "rgba(244,67,54,0.12) !important"
                : "rgba(244,67,54,0.08) !important",
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(244,67,54,0.12) !important"
                  : "rgba(244,67,54,0.08) !important",
              boxShadow: "none !important",
              transform: "none !important",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!parsedResult}
          onClick={handleUpload}
          sx={{ textTransform: "none", fontWeight: 500 }}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadHistoricalSignalsModal;
